import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
} from "@yazamco/shared-types";

/**
 * Auth API client (Phase 5.8). Tokens live in HttpOnly cookies set by the
 * server; these calls only carry credentials (cookies) + the CSRF header
 * (added by the httpClient interceptor). Errors expose the structured
 * `error.code` so the UI can show the right message.
 */

export interface AuthApiError extends Error {
  code: string;
  status: number;
}

async function parse(res: Response): Promise<{ code: string; message: string }> {
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string };
    };
    return {
      code: body.error?.code ?? "ERROR",
      message: body.error?.message ?? `Request failed (${res.status})`,
    };
  } catch {
    return { code: "ERROR", message: `Request failed (${res.status})` };
  }
}

function makeError(code: string, message: string, status: number): AuthApiError {
  const err = new Error(message) as AuthApiError;
  err.code = code;
  err.status = status;
  return err;
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch("/api/v1/me");
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /api/v1/me failed: ${res.status}`);
  return (await res.json()) as MeResponse;
}

export async function login(input: LoginRequest): Promise<LoginResponse> {
  const res = await fetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const { code, message } = await parse(res);
    throw makeError(code, message, res.status);
  }
  return (await res.json()) as LoginResponse;
}

export async function logout(): Promise<void> {
  await fetch("/api/v1/auth/logout", { method: "POST" });
}
